// lib/orientation.js — Pré-onboarding public (spec CTO §4-6, §12).
// Recommandation DÉTERMINISTE et explicable (pas d'IA au MVP). Ne crée aucune
// donnée métier. Les réponses vivent en sessionStorage jusqu'à l'inscription.

export const ORIENTATION_STORAGE_KEY = "artkiv_orientation_v1";

// Profil « métier » (question commune). role = valeur profiles.role à l'inscription.
export const ORIENTATION_PROFILES = [
  { key: "collectionneur", role: "user" },
  { key: "advisor", role: "expert" },
  { key: "family_office", role: "family_office" },
  { key: "fondation", role: "user" }, // + foundation_mode (cf. spec §8)
];

// 3 questions max par profil. type : choice | multi | boolean.
export const ORIENTATION_QUESTIONS = {
  collectionneur: [
    { id: "artworks_count", type: "choice", opts: ["lt50", "b50_200", "b200_1000", "gt1000"] },
    { id: "needs", type: "multi", opts: ["inventory", "market", "ai_ocr", "insurance_conservation", "succession", "certification"] },
    { id: "collaboration", type: "choice", opts: ["me_only", "one_reader", "multiple"] },
  ],
  advisor: [
    { id: "clients_count", type: "choice", opts: ["b1_5", "b6_20", "b21_50", "gt50"] },
    { id: "launch_ai", type: "boolean" },
    { id: "pro_tools", type: "multi", opts: ["custom_reports", "white_label", "team", "unlimited_clients", "mission_tracking"] },
  ],
  family_office: [
    { id: "families_count", type: "choice", opts: ["b1_3", "b4_10", "gt10"] },
    { id: "inventory_state", type: "choice", opts: ["none", "csv", "pdf", "other_software"] },
    { id: "priorities", type: "multi", opts: ["overview", "valuation_reporting", "insurance_risk", "succession", "rights_actors", "erp"] },
  ],
  fondation: [
    { id: "collections_count", type: "choice", opts: ["one", "b2_5", "gt5"] },
    { id: "collaborators_count", type: "choice", opts: ["solo", "b2_5", "gt5"] },
    { id: "priorities", type: "multi", opts: ["inventory", "conservation", "loans", "exhibitions", "ocr_import", "reports_governance"] },
  ],
};

// Plan → prix + code plan attendu par Register (?plan=). Fondation : pas de prix
// inventé (spec §3) → Premium + mode Fondation, ou contact.
export const PLAN_META = {
  standard: { price: "29 €", registerPlan: "standard" },
  premium: { price: "199 €", registerPlan: "premium" },
  advisor: { price: "99 €", registerPlan: "art_advisor" },
  advisor_premium: { price: "199 €", registerPlan: "art_advisor_premium" },
  family_office: { price: "299 €", registerPlan: "family_office" },
  fondation: { price: "299 €", registerPlan: "fondation" },
};

function foScenario(answers) {
  if (answers.families_count === "gt10") return "foMeeting";
  if ((answers.priorities || []).includes("erp")) return "foErp";
  if (answers.inventory_state === "csv") return "foCsv";
  if (answers.inventory_state === "pdf") return "foPdf";
  if (answers.inventory_state === "other_software") return "foMigration";
  if (answers.inventory_state === "none") return "foStart";
  return null;
}

// Renvoie { plan, reasons[≤3], migrationRecommended, contactRecommended, foundationMode?, scenario?, alt? }.
export function recommendPlan(profileType, answers = {}) {
  if (profileType === "collectionneur") {
    const premiumNeeds = ["market", "ai_ocr", "insurance_conservation", "succession", "certification"];
    const needs = answers.needs || [];
    const hit = needs.filter((n) => premiumNeeds.includes(n));
    const multi = answers.collaboration === "multiple";
    const needsPremium = hit.length > 0 || multi;
    const reasons = [];
    if (needsPremium) {
      if (hit.includes("ai_ocr")) reasons.push("ai");
      if (hit.includes("market")) reasons.push("market");
      if (hit.includes("succession")) reasons.push("succession");
      if (hit.includes("insurance_conservation")) reasons.push("insurance");
      if (hit.includes("certification")) reasons.push("certification");
      if (multi) reasons.push("collaboration");
    } else {
      reasons.push("essentials", "inventory");
    }
    return {
      plan: needsPremium ? "premium" : "standard",
      reasons: reasons.slice(0, 3),
      migrationRecommended: answers.artworks_count === "gt1000",
      alt: needsPremium ? "standard" : null,
    };
  }

  if (profileType === "advisor") {
    const tools = answers.pro_tools || [];
    const manyClients = ["b6_20", "b21_50", "gt50"].includes(answers.clients_count);
    const ai = answers.launch_ai === true;
    const proHit = tools.filter((x) => ["white_label", "team", "unlimited_clients"].includes(x));
    const needsPremium = manyClients || ai || proHit.length > 0;
    const reasons = [];
    if (needsPremium) {
      if (manyClients) reasons.push("manyClients");
      if (ai) reasons.push("ai");
      if (proHit.includes("white_label")) reasons.push("whiteLabel");
      if (proHit.includes("team")) reasons.push("team");
      if (proHit.includes("unlimited_clients")) reasons.push("unlimited");
    } else {
      reasons.push("advisorBasic", "upToClients");
    }
    return {
      plan: needsPremium ? "advisor_premium" : "advisor",
      reasons: reasons.slice(0, 3),
      alt: needsPremium ? "advisor" : null,
    };
  }

  if (profileType === "family_office") {
    return {
      plan: "family_office",
      reasons: ["foConsolidated", "foReporting", "foSuccession"].slice(0, 3),
      migrationRecommended: !!answers.inventory_state && answers.inventory_state !== "none",
      contactRecommended: answers.families_count === "gt10" || (answers.priorities || []).includes("erp"),
      scenario: foScenario(answers),
    };
  }

  if (profileType === "fondation") {
    const complex = answers.collections_count !== "one" || answers.collaborators_count === "gt5";
    return {
      plan: complex ? null : "fondation",
      foundationMode: true,
      contactRecommended: complex,
      reasons: complex ? ["foundationComplex"] : ["foundationSmall", "foundationSuite"],
      alt: null,
    };
  }

  return { plan: null, contactRecommended: true, reasons: [] };
}

// Construit l'URL d'inscription contextualisée (?role=&plan=). Le serveur revalide
// (spec §8) — l'URL n'est pas une source fiable. Fondation non-complexe → Premium.
export function buildRegisterUrl(profileType, rec) {
  const role = (ORIENTATION_PROFILES.find((p) => p.key === profileType) || {}).role || "user";
  const planCode = rec.plan ? (PLAN_META[rec.plan]?.registerPlan || rec.plan) : "premium";
  const params = new URLSearchParams({ role, plan: planCode, from: "orientation" });
  if (rec.foundationMode) params.set("foundation", "1");
  return `/register?${params.toString()}`;
}

export function saveOrientation(profileType, answers, rec) {
  try {
    sessionStorage.setItem(
      ORIENTATION_STORAGE_KEY,
      JSON.stringify({
        version: 1,
        profile_type: profileType,
        answers,
        recommended_plan: rec.plan,
        migration_recommended: !!rec.migrationRecommended,
        contact_recommended: !!rec.contactRecommended,
        created_at: new Date().toISOString(),
      })
    );
  } catch {
    /* sessionStorage indisponible : non bloquant */
  }
}

export function loadOrientation() {
  try {
    const raw = sessionStorage.getItem(ORIENTATION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
